import React from "react";

import { trDts } from "@modules/datasheets/utils/i18n";

import { useMessage } from "@common/hooks/message";
import { HttpErrorDto } from "@common/dto/http";
import { DatasheetTableReportFormat, DatasheetTableReportRow, DatasheetSettingsConfigureTableProps } from "./configure-table.types";
import { Flex } from "@common/components/Flex";
import { FormControl } from "@common/components/form-control";

const SET_DEFAULT_FORMAT_OPTION_VALUE = "__set-default-format__";

export const useController = (props: DatasheetSettingsConfigureTableProps) => {
  const {
    onSubmit,
    tableFormats,
    dropdownFormats,
    defaultTableFormats,
    selectedDropdownFormatId,
    onSelectedFormatChange,
    onSetDefaultFormat,
    onTableRowsChange,
  } = props;
  const { closeMessage, showErrorMessage, showInfoMessage, messageProps } = useMessage();

  const [internalTableFormats, setInternalTableFormats] = React.useState<Array<DatasheetTableReportRow>>(
    []
  );
  const [hasLocalEdits, setHasLocalEdits] = React.useState(false);

  const lastNotifiedFormatIdRef = React.useRef<DatasheetTableReportFormat["reportNumber"] | "" | undefined>(
    selectedDropdownFormatId ?? undefined
  );
  const previousSelectedFormatIdRef = React.useRef<DatasheetTableReportFormat["reportNumber"] | "" | undefined>(
    selectedDropdownFormatId ?? undefined
  );
  const onSelectedFormatChangeRef = React.useRef(onSelectedFormatChange);

  const areRowsEqual = React.useCallback(
    (left: Array<DatasheetTableReportRow>, right: Array<DatasheetTableReportRow>) => {
      if (left === right) {
        return true;
      }
      if (left.length !== right.length) {
        return false;
      }
      for (let index = 0; index < left.length; index += 1) {
        const leftRow = left[index];
        const rightRow = right[index];
        if (!leftRow || !rightRow) {
          return false;
        }
        if (leftRow.order !== rightRow.order || leftRow.width !== rightRow.width) {
          return false;
        }
        if (leftRow.subfield.fieldId !== rightRow.subfield.fieldId) {
          return false;
        }
      }
      return true;
    },
    []
  );

  const [formatNameOpened, setFormatNameOpened] = React.useState(false);

  React.useEffect(() => {
    onSelectedFormatChangeRef.current = onSelectedFormatChange;
  }, [onSelectedFormatChange]);

  const openFormatName = React.useCallback(() => {
    const validation = validateTableFormats(internalTableFormats);
    if (validation) {
      showErrorMessage(validation);
      return;
    }
    setFormatNameOpened(true);
  }, [internalTableFormats, showErrorMessage]);

  const closeFormatName = React.useCallback(() => {
    setFormatNameOpened(false);
  }, []);

  React.useEffect(() => {
    if (hasLocalEdits) {
      return;
    }
    setInternalTableFormats((prev) => (areRowsEqual(prev, tableFormats) ? prev : tableFormats));
  }, [areRowsEqual, hasLocalEdits, tableFormats]);

  const [selectedFormatId, setSelectedFormatId] = React.useState<
    DatasheetTableReportFormat["reportNumber"] | "" | undefined
  >(selectedDropdownFormatId ?? undefined);

  const formatDropdownOptions = React.useMemo(
    () => [
      { value: "", label: trDts("None") },
      ...dropdownFormats.map((format) => ({ value: format.reportNumber, label: format.reportName })),
      {
        value: SET_DEFAULT_FORMAT_OPTION_VALUE,
        label: trDts("Set Current Report as Default"),
        isDisabled: selectedFormatId == null || selectedFormatId === "",
      },
    ],
    [dropdownFormats, selectedFormatId]
  );

  React.useEffect(() => {
    if (selectedDropdownFormatId !== undefined) {
      setSelectedFormatId(selectedDropdownFormatId);
    }
  }, [selectedDropdownFormatId]);

  const getSuccessMessage = React.useCallback((response: unknown): string | null => {
    if (!response) {
      return null;
    }
    if (typeof response === "string") {
      return response;
    }
    if (typeof response === "object") {
      const payload = response as Record<string, unknown>;
      const message = payload.message ?? payload.msg;
      if (typeof message === "string") {
        return message;
      }
      if (typeof payload.data === "string") {
        return payload.data;
      }
      if (payload.data && typeof payload.data === "object") {
        const data = payload.data as Record<string, unknown>;
        const dataMessage = data.message ?? data.msg;
        if (typeof dataMessage === "string") {
          return dataMessage;
        }
      }
    }
    return null;
  }, []);

  const handleDropdownChange = React.useCallback(
    async (nextValue: DatasheetTableReportFormat["reportNumber"] | "" | string | undefined) => {
      if (nextValue === SET_DEFAULT_FORMAT_OPTION_VALUE) {
        if (selectedFormatId == null || selectedFormatId === "") {
          showErrorMessage(trDts("selectFormatMessage"));
          return;
        }
        if (!onSetDefaultFormat) {
          return;
        }
        try {
          const response = await onSetDefaultFormat(Number(selectedFormatId));
          const successMessage = getSuccessMessage(response);
          if (successMessage) {
            showInfoMessage(successMessage);
          }
        } catch (err) {
          console.error(err);
          showErrorMessage((err as HttpErrorDto)?.entity || trDts("Failed to set default format"));
        }
        return;
      }
      setSelectedFormatId(nextValue as DatasheetTableReportFormat["reportNumber"] | "");
    },
    [getSuccessMessage, onSetDefaultFormat, selectedFormatId, showErrorMessage, showInfoMessage]
  );

  React.useEffect(() => {
    const selectedFormatChanged = previousSelectedFormatIdRef.current !== selectedFormatId;
    previousSelectedFormatIdRef.current = selectedFormatId;

    if (!selectedFormatChanged && hasLocalEdits) {
      return;
    }

    if (selectedFormatId === "") {
      setHasLocalEdits(false);
      setInternalTableFormats((prev) => (areRowsEqual(prev, defaultTableFormats) ? prev : defaultTableFormats));
      if (lastNotifiedFormatIdRef.current !== selectedFormatId) {
        onSelectedFormatChangeRef.current?.(undefined);
        lastNotifiedFormatIdRef.current = selectedFormatId;
      }
      return;
    }
    if (selectedFormatId == null) {
      return;
    }
    const selectedFormat = dropdownFormats.find((format) => format.reportNumber === selectedFormatId);
    if (selectedFormat) {
      setHasLocalEdits(false);
      setInternalTableFormats((prev) => (areRowsEqual(prev, selectedFormat.rows) ? prev : selectedFormat.rows));
      if (lastNotifiedFormatIdRef.current !== selectedFormatId) {
        onSelectedFormatChangeRef.current?.(selectedFormat);
        lastNotifiedFormatIdRef.current = selectedFormatId;
      }
    }
  }, [areRowsEqual, selectedFormatId, dropdownFormats, defaultTableFormats, hasLocalEdits]);

  const [configureTableSubmitting, setConfigureTableSubmitting] = React.useState(false);

  const isAllowedToSubmit = !configureTableSubmitting;

  const handleSubmit = async (formatName: string) => {
    try {
      setConfigureTableSubmitting(true);
      await onSubmit({
        selectedDropdownFormatId: selectedFormatId === "" ? undefined : selectedFormatId,
        formats: internalTableFormats,
        formatName
      });
    } catch (err) {
      console.error(err);
      showErrorMessage((err as HttpErrorDto)?.entity || trDts("Failed to get update configure table"));
    } finally {
      setConfigureTableSubmitting(false);
    }
  };

  const handleFormatOrderChange = React.useCallback(
    (fieldId: number, e: React.ChangeEvent<HTMLInputElement>) => {
      const nextValue = e.target.value;
      setInternalTableFormats((prev) => {
        const nextRows = prev.map((row) =>
          row.subfield.fieldId === fieldId ? { ...row, order: nextValue } : row
        );
        onTableRowsChange?.(nextRows);
        return nextRows;
      });
      setHasLocalEdits(true);
    },
    [onTableRowsChange]
  );

  const validateTableFormats = React.useCallback((formats: Array<DatasheetTableReportRow>) => {
    const orders = formats.map((row) => String(row.order ?? "").trim());
    if (orders.some((order) => order.length === 0)) {
      return trDts("Each sub-field needs to have a numeric value. Please enter 0 for the sub-field to not display.");
    }

    const parsedOrders = orders.map((order) => Number(order));
    if (parsedOrders.some((order) => Number.isNaN(order) || !Number.isFinite(order) || !Number.isInteger(order) || order < 0)) {
      return trDts("Each sub-field needs to have a numeric value. Please enter 0 for the sub-field to not display.");
    }

    const seenOrders = new Set<number>();
    for (const order of parsedOrders) {
      if (order === 0) {
        continue;
      }
      if (seenOrders.has(order)) {
        return trDts("This number has already been used in the Order, please enter a different number");
      }
      seenOrders.add(order);
    }
    return null;
  }, []);

  const formatTablesHeader: Array<{
    title: string;
    style: React.CSSProperties;
    component: (params: { item: DatasheetTableReportRow }) => React.ReactNode;
  }> = React.useMemo(
    () => [
      {
        title: trDts("Order"),
        style: { width: "70px", fontWeight: 600, textAlign: "center" },
        component: ({ item }) => (
          <FormControl
            style={{ width: "70px" }}
            value={item.order ?? ""}
            type="number"
            onChange={(e) => handleFormatOrderChange(item.subfield.fieldId, e)}
            min={0}
          />
        ),
      },
      {
        title: trDts("Field Type"),
        style: { width: "70px", fontWeight: 600, textAlign: "center" },
        component: ({ item }) => (
          <Flex width="80px" alignItems="center" justify="center">
            <img style={{ height: "16px" }} src={item.subfield.imageURL} />
          </Flex>
        ),
      },
      {
        title: trDts("Field Name"),
        style: { fontWeight: 600, textAlign: "center", marginLeft: "10px" },
        component: ({ item }) => <Flex alignItems="center">{item.subfield.fieldName}</Flex>,
      },
    ],
    [handleFormatOrderChange]
  );

  return {
    isAllowedToSubmit,
    handleSubmit,
    configureTableSubmitting,
    formatTablesHeader,
    setSelectedFormatId,
    selectedFormatId,
    formatDropdownOptions,
    handleDropdownChange,
    closeMessage,
    messageProps,
    internalTableFormats,
    formatNameOpened,
    openFormatName,
    closeFormatName,
  };
};
